import { AsyncPipe, NgTemplateOutlet, UpperCasePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, input, OnInit, output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatOption } from '@angular/material/core';
import { MatFormField } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatSelect } from '@angular/material/select';
import { DefaultSelectionValue, FormGroupValue, InputPropOption, InputProps } from '@miq/shared-models';
import { L10nTranslatePipe } from 'angular-l10n';
import { ReplaySubject } from 'rxjs';
import { FilterBarLabelComponent } from '../filter-bar-label/filter-bar-label.component';

export function filterMultiSelectOptions(
  options: InputPropOption[],
  selectedValues: unknown[],
  dataKey: string,
  filterValue: string
): InputPropOption[] {
  const search = filterValue?.toLowerCase();
  if (!search) {
    return options.slice();
  }

  const selectedValueSet = new Set(selectedValues);
  const selectedOptions = options.filter(option => selectedValueSet.has(option[dataKey]));
  const matchingOptions = options.filter(option =>
    String(option.value ?? option.key ?? '')
      .toLowerCase()
      .includes(search)
  );

  return [
    ...selectedOptions,
    ...matchingOptions.filter(option => !selectedValueSet.has(option[dataKey])),
  ];
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'miq-searchable-multi-select',
  templateUrl: './searchable-multi-select.component.html',
  styleUrls: [
    '../simple-select/simple-select.component.scss',
    './searchable-multi-select.component.scss',
  ],
  imports: [
    FilterBarLabelComponent,
    MatFormField,
    MatSelect,
    FormsModule,
    ReactiveFormsModule,
    NgTemplateOutlet,
    MatOption,
    MatIcon,
    AsyncPipe,
    UpperCasePipe,
    L10nTranslatePipe,
  ],
})
export class SearchableMultiSelectComponent<T = DefaultSelectionValue> implements OnInit {
  props = input.required<InputProps<T>>();
  value = input<any>();
  multi = input(true);
  hideTitle = input(false);
  disabled = input(false);
  selection = output<FormGroupValue<any>>();
  mainValueControl: FormControl;
  optionsFilterControl = new FormControl<string>('');
  filteredOptions = new ReplaySubject<InputPropOption[]>(1);
  dataKey = 'key';
  displayClearButton = false;
  private readonly destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    this.dataKey = this.props().dataKey || this.dataKey;
    this.mainValueControl = new FormControl(this.value(), this.props().controlOptions || {});
    this.displayClearButton = Array.isArray(this.value()) && this.value().length > 0;
    this.filteredOptions.next(this.props().options?.slice() || []);
    this.optionsFilterControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(filterValue => this.filterOptions(filterValue));
  }

  openedChange(isOpen: boolean): void {
    if (!isOpen) {
      this.selection.emit({ [this.props().field]: this.mainValueControl.value });
    }
  }

  clearFilter(): void {
    this.optionsFilterControl.setValue('');
    this.mainValueControl.setValue([]);
    this.displayClearButton = false;
    this.selection.emit({ [this.props().field]: this.mainValueControl.value });
  }

  clearSearchFilter(): void {
    this.optionsFilterControl.setValue('');
  }

  onKeyDown(event: KeyboardEvent): void {
    if (event.code === 'Space' || event.key === ' ') {
      event.stopPropagation();
    }
  }

  onSelectionChanged(): void {
    this.displayClearButton = this.mainValueControl.value?.length > 0;
  }

  private filterOptions(filterValue: string): void {
    const options = this.props().options;
    if (!options) {
      return;
    }

    const selectedValues = Array.isArray(this.mainValueControl?.value) ? this.mainValueControl.value : [];
    this.filteredOptions.next(filterMultiSelectOptions(options, selectedValues, this.dataKey, filterValue));
  }
}